import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { cloneDeep } from 'lodash';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { SignHelper } from 'src/app/helpers/sign-helper';
import { MatDialogHelper } from 'src/app/helpers/material/mat-dialog-helper';
import Company from 'src/app/model/company/company';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { Project } from 'src/app/model/project/project';
import { SignShapeTypesChoices } from 'src/app/model/sign/enums/sign-shape-type';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { SignPositionTypesChoices } from '../../../../../../model/sign/enums/sign-position-type';
import { IpBoardMeasurement } from '../helper/IpBoardMeasurement';
import { ClientNoStockComponent } from './client-no-stock/client-no-stock.component';
import { StockOrderedEventService } from './client-order-stock//helper/services/stock-orderered-event.service';
import { ClientOrderStockComponent } from './client-order-stock/client-order-stock.component';
import { StockOrderedEvent } from './client-order-stock/helper/services/client-order-stock-controller-request.service';
import { ClientSignSelectorHelper } from './client-sign-selector-helper';
import { ClientSignStockThanksComponent } from './client-sign-stock-thanks/client-sign-stock-thanks.component';
import { ClientSignStockWarningComponent } from './client-sign-stock-warning/client-sign-stock-warning.component';
import { SignForCompanyControllerRequestService } from './sign-for-company-controller-request.service';
import { SignWithStockAmount } from './sign-with-stock-amount';

@Component({
  selector: 'client-sign-selector',
  templateUrl: './client-sign-selector.component.html',
  styleUrls: ['./client-sign-selector.component.scss'],
})
export class ClientSignSelectorComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnChanges
{
  @Input()
  ipBoardPart: IPBoardPart;
  @Input()
  leftBoardMeasurement: IpBoardMeasurement;
  @Input()
  project: Project;
  @Input()
  verifySignSelection: boolean = true;

  @Input()
  title: string;
  @Input()
  leftRightBoard = false;
  @Input()
  isRightBoard = false;

  @Input()
  pairedSignId?: number;

  @Output()
  signSelected = new EventEmitter<any>();

  init: boolean = false;

  company: Company;

  signOptions: SignWithStockAmount[];
  allOptions: SignWithStockAmount[];
  selectedOption: SignWithStockAmount;
  customSignOption: SignWithStockAmount;

  customRecommendedCollaborativeOption: SignWithStockAmount;
  customAlternativeCollaborativeOption: SignWithStockAmount;

  SIGN_SHAPE_TYPE_CHOICES = SignShapeTypesChoices;

  private static readonly EXAMPLE_BASE_DIRECTORY = '/assets/client-ip-board';

  customSignLeftDisplayExample =
    ClientSignSelectorHelper.getBackgroundImageStyle(
      `${ClientSignSelectorComponent.EXAMPLE_BASE_DIRECTORY}/custombord.png`
    );
  customSignRightDisplayExample =
    ClientSignSelectorHelper.getBackgroundImageStyle(
      `${ClientSignSelectorComponent.EXAMPLE_BASE_DIRECTORY}/customkleefbord_vexip.png`
    );

  constructor(
    private signForCompanyControllerRequestService: SignForCompanyControllerRequestService,
    private stockOrderedEventService: StockOrderedEventService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private modalService: ModalService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['leftRightBoard'] && this.allOptions != null) {
      this.filterOptions();
    }
    if (this.allOptions != null && changes['leftBoardMeasurement']) {
      this.filterOptions();
    }
    if (
      this.allOptions != null &&
      changes.pairedSignId != null &&
      !changes.pairedSignId.isFirstChange()
    ) {
      this.filterOptions();
    }
  }

  ngOnInit(): void {
    this.initializeCustomSignOptions();
    if (this.ipBoardPart.id != null) {
      this.initializeSelectedSign();
    }
    this.calculateCompany();

    this.getSigns(this.company.id).catch((err) => console.error(err));

    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        const previousCompany = this.company;
        this.calculateCompany();
        if (previousCompany.id !== this.company.id) {
          this.getSigns(this.company.id).catch((err) => console.error(err));
        }
      }
    );

    this.addSubscription(
      this.stockOrderedEventService.getStockOrderedEventSubscribable(),
      (event: StockOrderedEvent) => {
        this.handleStockOrderedEvent(event);
      }
    );
  }

  private calculateCompany(): void {
    if (!CollectionHelper.isPopulated(this.project.projectContacts)) {
      throw new Error(`Geen bedrijf geselecteerd`);
    }
    this.company = this.project.projectContacts[0].company;
    if (this.company == null) {
      throw new Error(`Geen bedrijf geselecteerd`);
    }
  }

  private initializeCustomSignOptions(): void {
    this.customSignOption = new SignWithStockAmount();
    this.customSignOption.id = ClientSignSelectorHelper.CUSTOM_SIGN_OPTION_ID;
    this.customSignOption.displayExampleStyle =
      ClientSignSelectorHelper.getBackgroundImageStyle(
        `${ClientSignSelectorComponent.EXAMPLE_BASE_DIRECTORY}/custombord.png`
      );

    this.customRecommendedCollaborativeOption = new SignWithStockAmount();
    this.customRecommendedCollaborativeOption.id =
      ClientSignSelectorHelper.RECOMMENDED_COLLABORATIVE_SIGN_OPTION_ID;
    this.customRecommendedCollaborativeOption.displayExampleStyle =
      ClientSignSelectorHelper.getBackgroundImageStyle(
        `${ClientSignSelectorComponent.EXAMPLE_BASE_DIRECTORY}/custom-aanbevolen.png`
      );

    this.customAlternativeCollaborativeOption = new SignWithStockAmount();
    this.customAlternativeCollaborativeOption.id =
      ClientSignSelectorHelper.ALTERNATIVE_COLLABORATIVE_SIGN_OPTION_ID;
  }

  private async getSigns(companyId: number): Promise<void> {
    this.init = false;
    this.allOptions =
      await this.signForCompanyControllerRequestService.getForCompanyWithStockAmount(
        companyId
      );
    this.filterOptions();

    this.preprocessSignOptions();
    this.init = true;
  }

  private filterOptions() {
    this.signOptions = this.allOptions;
    if (this.leftRightBoard) {
      if (this.isRightBoard) {
        this.filterRight();
      } else {
        this.filterLeft();
      }
    }
  }

  filterRight() {
    if (
      this.leftBoardMeasurement != null &&
      this.leftBoardMeasurement.height != null &&
      this.leftBoardMeasurement.width != null
    ) {
      this.filterRightWithDimensions();
    } else {
      this.filterRightWithoutDimensions();
    }
  }

  //TODO: add pairedSignId filter + get pairedSignId from be
  filterRightWithDimensions() {
    if (this.signOptions == null || this.signOptions.length <= 0) {
      return;
    }
    let signMeasurement: IpBoardMeasurement = {
      height: 0,
      width: 0,
    };
    this.signOptions = this.signOptions.filter((sign) => {
      signMeasurement = SignHelper.getActualSizes(sign);
      return (
        signMeasurement != null &&
        (sign.position == null ||
          sign.position.value != SignPositionTypesChoices.LEFT.value) &&
        this.leftBoardMeasurement.height === signMeasurement.height &&
        this.leftBoardMeasurement.width === signMeasurement.width &&
        sign.id === this.pairedSignId
      );
    });
  }

  filterRightWithoutDimensions() {
    this.signOptions = this.signOptions.filter(
      (sign) =>
        (sign.position == null ||
          sign.position.value != SignPositionTypesChoices.LEFT.value) &&
        this.pairedSignId === sign.id
    );
  }

  filterLeft() {
    this.signOptions = this.signOptions.filter(
      (sign) =>
        sign.position == null ||
        sign.position.value != SignPositionTypesChoices.RIGHT.value
    );
  }

  private initializeSelectedSign(): void {
    this.ipBoardPart.signOptionSelected = true;
    if (this.ipBoardPart.sign == null) {
      if (!this.project.collaborative) {
        this.selectedOption = this.customSignOption;
      } else {
        this.selectedOption =
          (this.ipBoardPart.otherWidth === 100 &&
            this.ipBoardPart.otherHeight === 140) ||
          (this.ipBoardPart.otherWidth === 140 &&
            this.ipBoardPart.otherHeight === 100)
            ? this.customRecommendedCollaborativeOption
            : this.customAlternativeCollaborativeOption;
      }
    }
  }

  preprocessSignOptions(): void {
    if (
      this.ipBoardPart.id != null &&
      this.ipBoardPart.sign != null &&
      this.ipBoardPart.sign.archived
    ) {
      // in this case, we still want to show the archived sign as the selected sign, because otherwise
      // the user may be misled into thinking no sign has been selected at all
      const clonedArchivedSign = <SignWithStockAmount>(
        cloneDeep(this.ipBoardPart.sign)
      );
      this.signOptions.push(clonedArchivedSign);
    }
    if (CollectionHelper.isPopulated(this.signOptions)) {
      this.signOptions.forEach((o) => {
        ClientSignSelectorHelper.setDisplayExampleStyle(o);
        ClientSignSelectorHelper.setAmountStockClass(o);
      });
    }
    if (this.ipBoardPart.id != null && this.ipBoardPart.sign != null) {
      this.selectedOption = this.signOptions.find(
        (o) => o.id === this.ipBoardPart.sign.id
      );
    }
  }

  trySelectSign(value: SignWithStockAmount): void {
    const customToStandard = this.ipBoardPart.sign == null;
    if (!this.verifySignSelection) {
      this.doSelectSign(value);
      this.emitSelected(customToStandard);
      return;
    }
    const ordered =
      value.orderedAmount != null
        ? value.orderedAmount
        : value.inOfferStock != null
        ? value.inOfferStock
        : 0;
    const currentStock =
      (value.totalStock == null ? 0 : value.totalStock) +
      ordered +
      (value.inProductionStock == null ? 0 : value.inProductionStock);
    if (currentStock > 0 || value.withoutStock) {
      this.doSelectSign(value);
      if (
        value.minimumStock != null &&
        currentStock < value.minimumStock &&
        !value.withoutStock
      ) {
        this.openStockWarningDialog(value);
      }
      this.emitSelected(customToStandard);
    }

    if (currentStock <= 0 && !value.withoutStock) {
      this.openNoStockDialog(value);
    } else if (
      currentStock < 2 &&
      !value.withoutStock &&
      !this.leftRightBoard &&
      this.ipBoardPart != null &&
      this.ipBoardPart.boardType != null &&
      [
        BoardTypeChoices.DOUBLE_SIDED.ordinal,
        BoardTypeChoices.V.ordinal,
      ].includes(this.ipBoardPart.boardType.ordinal)
    ) {
      this.openNoStockDialog(value, true);
    }
  }

  resetSelectSign() {
    this.selectedOption = null;
    this.ipBoardPart.sign = null;
    this.ipBoardPart.signOptionSelected = false;
  }

  private doSelectSign(value: SignWithStockAmount) {
    this.selectedOption = value;
    if (this.ipBoardPart.id != null && this.ipBoardPart.sign == null) {
      IPBoardHelper.rememberTextContentsFromCustomSign(this.ipBoardPart);
    }
    this.ipBoardPart.sign = value;
    this.ipBoardPart.signOptionSelected = true;
    if (
      this.project.id != null &&
      CollectionHelper.isPopulated(this.project.subProjects)
    ) {
      const baseProject = ProjectHelper.getBaseProject(this.project);
      if (baseProject != null) {
        baseProject.stockShortageOnCreate = false;
      }
    }
    if (this.project.id != null) {
      const firstProduct = this.project.products[0];
      firstProduct.ipBoardCheckedPrintWork = value.withoutStock;
      firstProduct.needPrintwork =
        firstProduct.ipBoardCheckedPrintWork ||
        firstProduct.phoneNumberCheckedPrintWork ||
        firstProduct.textPrintworkChecked ||
        firstProduct.extraBoardCheckedPrintWork ||
        firstProduct.stickerCheckedPrintWork ||
        firstProduct.arrowCheckedPrintWork ||
        firstProduct.secondBoardCheckedPrintWork ||
        firstProduct.otherOrderCheckedPrintWork;
    }
  }

  setCustomSign(): void {
    this.selectedOption = this.customSignOption;
    const standardToCustomSign = this.ipBoardPart.sign != null;
    if (this.ipBoardPart.id != null && this.ipBoardPart.sign != null) {
      IPBoardHelper.rememberTextContents(this.ipBoardPart);
    }
    this.ipBoardPart.sign = null;
    this.ipBoardPart.signOptionSelected = true;
    if (this.leftRightBoard && this.isRightBoard) {
      this.ipBoardPart.otherHeight = this.leftBoardMeasurement.height;
      this.ipBoardPart.otherWidth = this.leftBoardMeasurement.width;
    } else {
      this.emitSelected(standardToCustomSign);
    }
    if (this.project.id != null) {
      const firstProduct = this.project.products[0];
      firstProduct.ipBoardCheckedPrintWork = true;
      firstProduct.needPrintwork = true;
    }
  }

  setRecommendedCollaborativeSign(): void {
    this.selectedOption = this.customRecommendedCollaborativeOption;
    this.ipBoardPart.sign = null;
    this.ipBoardPart.signOptionSelected = true;
    this.emitSelected(true);
  }

  setAlternativeCollaborativeSign(): void {
    this.selectedOption = this.customAlternativeCollaborativeOption;
    this.ipBoardPart.sign = null;
    this.ipBoardPart.signOptionSelected = true;
    this.emitSelected(true);
  }

  private emitSelected(toStandardOrToCustom = false): void {
    if (this.isRightBoard) {
      this.projectDisplayObservableService
        .getRightSignSelectedSubscribable()
        .next({
          ipBoardPart: this.ipBoardPart,
          toStandardOrToCustom: toStandardOrToCustom,
          adhesiveStockSign:
            this.selectedOption != null &&
            this.selectedOption.id > 0 &&
            SignHelper.stockSignIsAdhesive(this.selectedOption),
          selectedOptionId:
            this.selectedOption != null ? this.selectedOption.id : null,
        });
    }
    this.projectDisplayObservableService.getSignSelectedSubscribable().next({
      ipBoardPart: this.ipBoardPart,
      toStandardOrToCustom: toStandardOrToCustom,
      adhesiveStockSign:
        this.selectedOption != null &&
        this.selectedOption.id > 0 &&
        SignHelper.stockSignIsAdhesive(this.selectedOption),
      selectedOptionId:
        this.selectedOption != null ? this.selectedOption.id : null,
    });
    this.projectDisplayObservableService
      .getStockInfoUpdatedSubscribable()
      .next();
    this.signSelected.emit();
  }

  openOrderStockDialog(value: SignWithStockAmount): void {
    this.modalService.openComponentInModal(
      ClientOrderStockComponent,
      [
        { key: 'sign', value: value },
        {
          key: 'companyId',
          value: this.project.projectContacts[0].company.id,
        },
      ],
      new ObservableHandlerPair(
        (modalRef) =>
          MatDialogHelper.getClosedObservable(modalRef, 'stockOrdered'),
        (event: StockOrderedEvent) => {
          if (event != null) {
            this.stockOrderedEventService
              .getStockOrderedEventSubscribable()
              .next(event);
            this.openStockThanksDialog(event);
          }
        }
      ),
      { minWidth: 850, panelClass: 'border-modal-panel-class' }
    );
  }

  private handleStockOrderedEvent(event: StockOrderedEvent): void {
    const matchingSign = this.signOptions.find((o) => o.id === event.signId);
    if (matchingSign != null) {
      const sum = event.stockIncreases.reduce((a, b) => a + b, 0);
      matchingSign.orderedAmount =
        matchingSign.orderedAmount == null
          ? sum
          : matchingSign.orderedAmount + sum;
      matchingSign.orderedAmount =
        matchingSign.orderedAmount + matchingSign.inOfferStock;
      this.trySelectSign(matchingSign);
    }
  }

  private openStockWarningDialog(sign: SignWithStockAmount): void {
    this.modalService.openComponentInModal(
      ClientSignStockWarningComponent,
      [],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.intendOrderStock,
        (intendOrderStock: boolean) => {
          if (intendOrderStock) {
            this.openOrderStockDialog(sign);
          }
        }
      ),
      { maxWidth: 500, panelClass: 'border-modal-panel-class' }
    );
  }

  private openNoStockDialog(
    sign: SignWithStockAmount,
    isDoubleSided: boolean = false
  ): void {
    this.modalService.openComponentInModal(
      ClientNoStockComponent,
      [{ key: 'isDoubleSided', value: this.project }],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.intendOrderStock,
        (res: { intendOrderStock: boolean; autoSelectNoStock: boolean }) => {
          if (res.intendOrderStock) {
            this.openOrderStockDialog(sign);
          } else if (res.autoSelectNoStock) {
            this.setCustomSign();
          }
        }
      ),
      { maxWidth: 550, panelClass: 'border-modal-panel-class' }
    );
  }

  private openStockThanksDialog(event: StockOrderedEvent): void {
    this.modalService.openComponentInModal(
      ClientSignStockThanksComponent,
      [{ key: 'isDirectOrder', value: event.isDirectOrder }],
      null,
      { panelClass: 'thanks-modal-panel-class', disableClose: true }
    );
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
